export const QUESTIONNAIRE_SECTIONS = [
  {
    id: 1,
    title: "Your Business",
    icon: "Briefcase",
    consultantMessage: "First, tell us a little about your business, what you offer and where you’d most like to grow.\n\nThe more you tell us, the more useful our recommendations will be.",
    fields: [
      {
        name: "businessName",
        label: "Business Name",
        type: "text",
        width: "half",
        required: true,
        helperText: "Enter the legal name of your company or the brand name you use with customers."
      },
      {
        name: "contactEmail",
        label: "Contact Email Address",
        type: "text",
        width: "half",
        required: true,
        placeholder: "you@company.com",
        helperText: "We will use this to send your completed growth strategy."
      },
      {
        name: "websiteUrl",
        label: "Website URL",
        type: "text",
        width: "half",
        helperText: "Copy and paste the full web address (e.g., https://www.yourbusiness.com)."
      },
      {
        name: "industry",
        label: "What industry or type of business are you in?",
        type: "text",
        width: "half",
        helperText: "State your main field, such as 'Retail', 'SaaS', or 'Consulting'."
      },
      {
        name: "teamSize",
        label: "How many people work in the business?",
        type: "text",
        width: "half",
        helperText: "Include yourself plus all full-time and part-time staff."
      },
      {
        name: "geography",
        label: "Where are your customers based?",
        type: "text",
        width: "half",
        placeholder: "For example: local area, UK-wide, Europe or worldwide.",
        helperText: "Specify the main cities, regions, or countries you serve."
      },
      {
        name: "businessType",
        label: "Who do you mainly sell to?",
        type: "select",
        width: "half",
        options: ["Other businesses (B2B)", "Consumers (B2C)", "Both businesses and consumers"],
        helperText: "Select the option that represents the majority of your revenue."
      },
      {
        name: "productsOffered",
        label: "What products or services do you sell?",
        type: "textarea",
        width: "full",
        helperText: "List your top 3-5 most important products or services."
      },
      {
        name: "highestMargin",
        label: "Which products or services make you the most profit?",
        type: "textarea",
        width: "full",
        placeholder: "If you're not sure, an estimate is fine.",
        helperText: "Focus on the items with the biggest gap between cost and sale price."
      },
      {
        name: "growthFocus",
        label: "Which products or services would you most like to sell more of?",
        type: "textarea",
        width: "full",
        helperText: "Identify the specific service you want to lead your next marketing campaign."
      },
      {
        name: "differentiation",
        label: "Why do your best customers choose you instead of someone else?",
        type: "textarea",
        width: "full",
        placeholder: "Think about what customers say they value most about buying from or working with you.",
        helperText: "Describe the specific reason you win business over competitors."
      },
      {
        name: "growthTrigger",
        label: "Why is growing the business a priority for you right now?",
        type: "textarea",
        width: "full",
        placeholder: "For example: sales have slowed, you have more capacity, you are launching something new, or you want to reach a new type of customer.",
        helperText: "Explain the internal or external event that prompted you to look for growth now."
      }
    ]
  },
  {
    id: 2,
    title: "Where You Want to Go",
    icon: "Target",
    consultantMessage: "Next, tell us what you want the business to achieve and what is making growth difficult right now.",
    fields: [
      {
        name: "twelveMonthGoals",
        label: "What would you like the business to achieve over the next 12 months?",
        type: "textarea",
        width: "full",
        placeholder: "For example: more revenue, more customers, more enquiries, new markets or new services.",
        helperText: "List 2-3 specific milestones you want to reach by this time next year."
      },
      {
        name: "priorities",
        label: "What are the three most important things for the business right now?",
        type: "textarea",
        width: "full",
        helperText: "Rank your top 3 immediate goals in order of importance."
      },
      {
        name: "successVision",
        label: "What would need to happen over the next 12 months for you to feel it had been a successful year?",
        type: "textarea",
        width: "full",
        helperText: "Describe the 'ideal state' your business should be in 12 months from now."
      },
      {
        name: "limitingGrowth",
        label: "What is currently making it difficult to grow the business?",
        type: "textarea",
        width: "full",
        placeholder: "For example: not enough enquiries, low conversion, lack of time, team capacity, pricing, cash flow or competition.",
        helperText: "Identify the single biggest bottleneck preventing you from scaling."
      }
    ]
  },
  {
    id: 3,
    title: "Your Customers",
    icon: "Users",
    consultantMessage: "Tell us about the customers you most want to attract and what they need from you.",
    fields: [
      {
        name: "idealCustomer",
        label: "What does your ideal customer look like?",
        type: "textarea",
        width: "full",
        helperText: "Describe the person or business that buys from you effortlessly."
      },
      {
        name: "customerProfile",
        label: "How would you describe your ideal customer?",
        type: "textarea",
        width: "full",
        placeholder: "For businesses: industry, size, job role and location. For consumers: age range, location and any other useful details.",
        helperText: "Provide demographic and firmographic details."
      },
      {
        name: "problemsSolved",
        label: "What problem are customers trying to solve when they come to you?",
        type: "textarea",
        width: "full",
        helperText: "Focus on their pain points."
      },
      {
        name: "purchaseTrigger",
        label: "What usually makes a customer contact you or decide they need your product or service?",
        type: "textarea",
        width: "full",
        helperText: "What is the catalyst event that drives them to buy?"
      },
      {
        name: "objections",
        label: "What questions or concerns do customers usually have before they buy?",
        type: "textarea",
        width: "full",
        helperText: "e.g., Price, implementation time, trust, etc."
      }
    ]
  },
  {
    id: 4,
    title: "How You Win Business",
    icon: "TrendingUp",
    consultantMessage: "Now tell us what usually happens from the moment someone gets in touch to the point they become a customer.",
    fields: [
      {
        name: "leadEntry",
        label: "How do new enquiries usually reach you?",
        type: "textarea",
        width: "full",
        placeholder: "For example: phone, email, website form, referral, social media or in person.",
        helperText: "Where does your inbound traffic originate?"
      },
      {
        name: "postInquiry",
        label: "What usually happens after someone makes an enquiry?",
        type: "textarea",
        width: "full",
        helperText: "Describe your sales funnel steps."
      },
      {
        name: "closeRate",
        label: "Out of every 10 genuine enquiries, roughly how many become customers?",
        type: "text",
        width: "full",
        placeholder: "If you don't know, that's fine.",
        helperText: "Your estimated conversion rate."
      },
      {
        name: "salesCycle",
        label: "How long does it usually take for an enquiry to become a sale?",
        type: "text",
        width: "full",
        placeholder: "For example: same day, a few days, a few weeks or several months.",
        helperText: "Helps us plan campaign timelines."
      },
      {
        name: "nonConversionReasons",
        label: "Why do people sometimes decide not to buy?",
        type: "textarea",
        width: "full",
        helperText: "What kills the deal?"
      },
      {
        name: "followUp",
        label: "What do you do if someone enquires but does not buy straight away?",
        type: "textarea",
        width: "full",
        helperText: "Describe your nurturing process."
      }
    ]
  },
  {
    id: 5,
    title: "Marketing Today",
    icon: "BarChart2",
    consultantMessage: "Tell us what marketing you are doing today, what seems to work and what you currently measure.",
    fields: [
      {
        name: "currentChannels",
        label: "How do you currently promote the business?",
        type: "textarea",
        width: "full",
        placeholder: "For example: Google, social media, email, SEO, networking, referrals, advertising or events.",
        helperText: "List all active marketing channels."
      },
      {
        name: "channelPerformance",
        label: "Which marketing seems to work best, and which seems to work least well?",
        type: "textarea",
        width: "full",
        helperText: "Your honest assessment of current ROI."
      },
      {
        name: "adSpend",
        label: "Roughly how much do you currently spend on advertising each month?",
        type: "text",
        width: "full",
        placeholder: "If you don't know, an estimate is fine.",
        helperText: "Include ad spend only, excluding agency fees."
      },
      {
        name: "marketingActivity",
        label: "What marketing are you actively doing at the moment?",
        type: "textarea",
        width: "full",
        placeholder: "For example: Google Ads, Meta Ads, SEO, email, social media, content, events or partnerships.",
        helperText: "Current active campaigns."
      },
      {
        name: "analytics",
        label: "Do you currently track your website or marketing performance?",
        type: "text",
        width: "full",
        placeholder: "For example: Google Analytics, Search Console, advertising reports or CRM reports. If not, just say no.",
        helperText: "Helps us know what data we can rely on."
      },
      {
        name: "metricsKnown",
        label: "What results do you currently measure?",
        type: "textarea",
        width: "full",
        placeholder: "For example: website visitors, enquiries, sales, conversion rate or advertising results. If you don't currently measure these, that's fine.",
        helperText: "List KPIs you monitor regularly."
      },
      {
        name: "marketingLearnings",
        label: "What marketing has worked well for you in the past, and what has felt like poor value?",
        type: "textarea",
        width: "full",
        helperText: "Share historical lessons learned."
      }
    ]
  },
  {
    id: 6,
    title: "Competition & Positioning",
    icon: "Crosshair",
    consultantMessage: "Help us understand how you want the business to be seen and who customers compare you with.",
    fields: [
      {
        name: "perception",
        label: "How would you like customers to see your business?",
        type: "textarea",
        width: "full",
        helperText: "Your desired brand image."
      },
      {
        name: "positioning",
        label: "Which of these best describes how you want your business to be seen?",
        type: "select",
        width: "full",
        options: ["Premium", "Good value", "Luxury", "Specialist", "Local expert", "Market leader", "Challenger / alternative to established competitors"],
        helperText: "Choose your primary market position."
      },
      {
        name: "customerFit",
        label: "What type of customers would you like more of, and which types are not a good fit?",
        type: "textarea",
        width: "full",
        helperText: "Define your ideal vs non-ideal clients."
      },
      {
        name: "mainCompetitors",
        label: "Who do customers compare you with most often?",
        type: "textarea",
        width: "full",
        helperText: "Name your top 3 competitors."
      },
      {
        name: "competitorWins",
        label: "When a customer chooses a competitor instead of you, do you know who they choose and why?",
        type: "textarea",
        width: "full",
        helperText: "Honest assessment of competitive losses."
      }
    ]
  },
  {
    id: 7,
    title: "Budget, Team & Capacity",
    icon: "Settings",
    consultantMessage: "Tell us what budget, people and capacity you have available so any recommendations are realistic.",
    fields: [
      {
        name: "monthlyBudget",
        label: "How much could you realistically invest in marketing each month?",
        type: "text",
        width: "full",
        placeholder: "An approximate monthly figure is fine.",
        helperText: "This grounds the strategy in reality."
      },
      {
        name: "internalTeam",
        label: "Who currently looks after marketing, sales or digital work in the business?",
        type: "textarea",
        width: "full",
        placeholder: "For example: you, an employee, freelancer, agency or nobody at the moment.",
        helperText: "Details your internal execution capacity."
      },
      {
        name: "fulfillmentCapacity",
        label: "If you suddenly had more customers next month, could you comfortably handle the extra work?",
        type: "select",
        width: "full",
        options: ["Yes — we have plenty of capacity", "Yes — with some adjustments", "Possibly", "No — we’re already near capacity", "Not sure"],
        helperText: "Measures operational readiness for growth."
      },
      {
        name: "bottlenecks",
        label: "Is there anything inside the business that would make it difficult to handle more customers?",
        type: "textarea",
        width: "full",
        helperText: "Identify operational choke points."
      },
      {
        name: "seasonalTrends",
        label: "Are there times of year when the business is much busier or quieter?",
        type: "textarea",
        width: "full",
        helperText: "Helps us plan campaign timing."
      }
    ]
  },
  {
    id: 8,
    title: "Commercial Picture",
    icon: "BarChart2",
    consultantMessage: "A few simple commercial figures help us recommend the right priorities. Estimates are absolutely fine.",
    fields: [
      {
        name: "averageCustomerValue",
        label: "Roughly how much does an average customer spend with you?",
        type: "text",
        width: "full",
        placeholder: "An estimate is fine.",
        helperText: "Lifetime Value (LTV) if known."
      },
      {
        name: "averageFirstPurchase",
        label: "Roughly how much is a typical first order, project or contract worth?",
        type: "text",
        width: "full",
        placeholder: "An estimate is fine.",
        helperText: "Initial transaction value."
      },
      {
        name: "repeatFrequency",
        label: "Do customers usually buy from you again? If so, how often?",
        type: "text",
        width: "full",
        helperText: "Details purchase frequency."
      },
      {
        name: "grossMargin",
        label: "Do you know roughly how much profit you make from an average sale or project?",
        type: "text",
        width: "full",
        placeholder: "If you don't know, that's fine. An estimate is enough.",
        helperText: "Margin percentage or figure."
      },
      {
        name: "revenueConcentration",
        label: "Does a large share of your income come from a small number of customers?",
        type: "text",
        width: "full",
        placeholder: "If yes, roughly how much? An estimate is fine.",
        helperText: "Helps assess revenue risk."
      }
    ]
  },
  {
    id: 9,
    title: "Final Thoughts",
    icon: "Key",
    consultantMessage: "Finally, tell us anything else that feels important from your point of view.",
    fields: [
      {
        name: "fixOneThing",
        label: "If you could improve just ONE thing in the business over the next 12 months, what would it be?",
        type: "textarea",
        width: "full",
        helperText: "Your absolute top priority."
      },
      {
        name: "keepsAwake",
        label: "What worries you most about growing the business?",
        type: "textarea",
        width: "full",
        helperText: "Your biggest fears or risks."
      },
      {
        name: "realReasonBuy",
        label: "What do you think customers value most about buying from you?",
        type: "textarea",
        width: "full",
        helperText: "Beyond the product itself."
      },
      {
        name: "anythingElse",
        label: "Is there anything important about the business, your customers or your market that we haven’t asked?",
        type: "textarea",
        width: "full",
        helperText: "Any final context we should know."
      }
    ]
  }
];